import * as React from 'react'
import { Loader2, Plus } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  useListIngestIntakesQuery,
  type IngestIntake
} from '@axilon-apps/domains-ontology-ui-state'

import { Empty } from '../../../components/primitives/Empty'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import type { LandingSite } from '../wizard/IngestLanding'
import { DeleteIntakeDialog } from './DeleteIntakeDialog'
import { IntakeDialog } from './IntakeDialog'
import { IntakesTable } from './IntakesTable'
import { INTAKE_PARAM } from './intake-view'

interface IntakesTabProps {
  sites: LandingSite[]
  /** The page header owns the "New intake" button, so creation state lives above this tab. */
  createOpen: boolean
  onCreateOpenChange: (open: boolean) => void
}

export function IntakesTab({ sites, createOpen, onCreateOpenChange }: IntakesTabProps) {
  // Files arrive without the page's involvement; refetch so the attention
  // counts are current whenever the tab opens.
  const intakesQuery = useListIngestIntakesQuery(undefined, { refetchOnMountOrArgChange: true })
  const [editing, setEditing] = React.useState<IngestIntake | null>(null)
  const [deleting, setDeleting] = React.useState<IngestIntake | null>(null)
  // The expanded row lives in the URL (`?intake=`) so a deep link opens it and
  // Back from a job, config, or merge review returns to the same row.
  const [searchParams, setSearchParams] = useSearchParams()
  const expandedId = searchParams.get(INTAKE_PARAM)
  const setExpandedId = React.useCallback(
    (id: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (id) next.set(INTAKE_PARAM, id)
          else next.delete(INTAKE_PARAM)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const intakes = intakesQuery.data?.intakes ?? []

  return (
    <>
      {intakesQuery.isLoading ? (
        <Card className="flex flex-row items-center gap-2 px-4 py-6 text-sm text-ink-3">
          <Loader2 className="size-4 animate-spin" />
          Loading intakes…
        </Card>
      ) : intakesQuery.isError ? (
        <Card className="gap-0 px-4 py-4">
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
            Intakes are unavailable.{' '}
            <button type="button" className="underline" onClick={() => void intakesQuery.refetch()}>
              Try again
            </button>
          </div>
        </Card>
      ) : intakes.length === 0 ? (
        <Card className="gap-0 py-0">
          <Empty
            title="No intakes yet"
            hint="Create one to give an outside system a stable address to push files to."
          />
          <div className="flex justify-center pb-6">
            <Button type="button" size="sm" onClick={() => onCreateOpenChange(true)}>
              <Plus className="size-3.5" />
              New intake
            </Button>
          </div>
        </Card>
      ) : (
        <IntakesTable
          intakes={intakes}
          sites={sites}
          expandedId={expandedId}
          onExpandedChange={setExpandedId}
          onEdit={setEditing}
          onDelete={setDeleting}
        />
      )}

      <IntakeDialog open={createOpen} onOpenChange={onCreateOpenChange} sites={sites} />
      <IntakeDialog
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        sites={sites}
        intake={editing}
      />
      <DeleteIntakeDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        intake={deleting}
      />
    </>
  )
}
